import {
  account,
  analytics,
  socialLogin,
  useSiteInfo,
} from '@silvertours/front-entities';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LoginContext } from './LoginState';

const LoginProvider = ({ children }: React.PropsWithChildren) => {
  const { accessToken } = account.useAccount();
  const loggedInStatus = Boolean(accessToken);
  const [isLoggedIn, setIsLoggedIn] = useState(loggedInStatus);
  const [showLogin, setShowLogin] = useState(false);
  const [showMobileLogin, setShowMobileLogin] = useState(false);
  const [username, setUsername] = useState('');
  const [error, setError] = useState<account.LoginError | null>(null);
  const [emailChosen, setEmailChosen] = useState(false);
  const { featureToggles } = useSiteInfo();

  /*
   * Derive this value, do not store it. The toggle arrives with the site
   * features, after the first render, so a stored initial value would keep the
   * email form for everyone.
   */
  const isProviderStep = featureToggles.isSocialLoginActive && !emailChosen;

  if (isLoggedIn !== loggedInStatus) {
    setIsLoggedIn(loggedInStatus);
  }

  const handleShowLogin = useCallback(
    (show: boolean) => {
      setShowLogin(show);
      // A gate that opens again starts on the provider buttons.
      setEmailChosen(false);
      if (show) {
        analytics.gtm.trackShowLogin({ isLoggedIn });
      }
    },
    [isLoggedIn],
  );

  const handleShowMobileLogin = useCallback(
    (show: boolean) => {
      setShowMobileLogin(show);
      // A gate that opens again starts on the provider buttons.
      setEmailChosen(false);
      if (show) {
        analytics.gtm.trackShowLogin({ isLoggedIn });
      }
    },
    [isLoggedIn],
  );

  /*
   * The gate closes when a session exists. The desktop overlay and the mobile
   * menu hold separate state, so close both.
   */
  const handleSocialLoginSuccess = useCallback(() => {
    setShowLogin(false);
    setShowMobileLogin(false);
  }, []);

  const {
    availableProviders,
    emailAddress,
    error: socialError,
    isProcessing: isSocialLoginProcessing,
    pendingProvider,
    start: startSocialLogin,
  } = socialLogin.useSocialLogin({
    placement: 'login_gate',
    onSuccess: handleSocialLoginSuccess,
  });

  /*
   * The email form shares this banner, so a provider failure must reach it the
   * same way an email failure does. The address goes with it: the unverified
   * email notice offers to send the confirmation again, and the user typed no
   * address on this path.
   */
  useEffect(() => {
    setError(socialError);

    if (socialError && emailAddress) {
      setUsername(emailAddress);
    }
  }, [emailAddress, socialError]);

  const value = useMemo(
    () => ({
      isLoggedIn,
      setIsLoggedIn,
      error,
      setError,
      showLogin,
      setShowLogin: handleShowLogin,
      showMobileLogin,
      setShowMobileLogin: handleShowMobileLogin,
      username,
      setUsername,
      availableProviders,
      pendingProvider,
      isSocialLoginProcessing,
      startSocialLogin,
      isProviderStep,
      setEmailChosen,
    }),
    [
      availableProviders,
      error,
      handleShowLogin,
      handleShowMobileLogin,
      isLoggedIn,
      isProviderStep,
      isSocialLoginProcessing,
      pendingProvider,
      showLogin,
      showMobileLogin,
      startSocialLogin,
      username,
    ],
  );

  return (
    <LoginContext.Provider value={value}>{children}</LoginContext.Provider>
  );
};

export { LoginProvider };
